import {
  ColumnManifestData,
  HeaderManifestData,
  OptionsClass,
  SectionManifestData
} from "@/domain/entities/MainApp";
import autoTable, { ColumnInput } from "jspdf-autotable"; // Add UserOptions import
import capitalize from "lodash/capitalize";
import { AccountController } from "@/app/ui/controllers/AccountController";
import JsBarcode from "jsbarcode";
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import { PDFDocument } from "./types/pdf-types";
import { Dispatch, DispatchStt } from "@/domain/entities/Dispatch";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import { convertQRCode } from "@/app/ui/modules/receipt-v2/module";
import { createI18n } from "vue-i18n";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";

interface DocumentState {
  doc: any;
  roleType: string;
  title: string;
  headerSection: OptionsClass[][];
  columnData: ColumnInput[];
  x: number;
  y: number;
  xx: number;
  nextSection: number;
  startY: number;
  logo: string;
  qrcodeValue: string;
  barcodeValue: string;
  currentSection: number;
}

const margin = {
  left: 8,
  right: 8,
  top: 4,
  bottom: 2
};

const sections = 1;
const spacing = 5;

let documentState: DocumentState = {
  doc: null,
  roleType: "",
  title: "",
  headerSection: [],
  columnData: [],
  x: margin.left,
  y: margin.top,
  xx: margin.left,
  nextSection: 1,
  startY: margin.top + 23,
  logo: "",
  qrcodeValue: "",
  barcodeValue: "",
  currentSection: 1
};

const mapCourierOrShuttleInfo = (data: HeaderManifestData) => {
  if (data.dispatchToHubName || data.dispatchToDriverPhone) {
    documentState.startY += 2;
  }

  if (data.dispatchToDriverId) {
    return `${data.dispatchToDriverId} - ${data.dispatchToDriverName} \n  ${data.dispatchToDriverPhone}`;
  }

  if (data.dispatchToShuttleId) {
    return `${data.dispatchToCityCode} - ${data.dispatchToShuttleName} \n  ${data.dispatchToHubName}`;
  }

  return "-";
}

const headerSectionProcess = async (params: SectionManifestData) => {
  const i18n = createI18n({
    legacy: false,
    locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my },
  });
  const t = i18n.global.t;

  // set accound data
  params.headerDetailData.accountData = AccountController.accountData;
  documentState.roleType =
    params.headerDetailData.accountData.account_type_detail?.type;
  documentState.title = `${params.featureName} (${params.headerDetailData.key.value})`;

  /* HEADER SECTION DATA */
  // array in array for divide header data. ex: [[first-section],[second-section],[third-section],[...-section]]
  const headerSectionsData: OptionsClass[] = [
    new OptionsClass({
      name: params.headerDetailData.key.name,
      value: params.headerDetailData.key.value,
      key: "key",
    }),
    new OptionsClass({
      name: t("dispatch.pdf.consolidatorCity"),
      value: params.headerDetailData.destinationCity,
      key: "destinationCity",
      meta: {
        valueGap: 25,
        sectionGap: 65
      }
    }),
    new OptionsClass({
      name: t("dispatch.pdf.totalBerat"),
      value: params.headerDetailData.totalGrossWeight.toString() + "kg",
      key: "totalPiece",
      meta: {
        valueGap: 20,
        sectionGap: 35
      }
    }),
    new OptionsClass({
      name: t("dispatch.pdf.manifestDate"),
      value:
        formatDateBasedTimezoneValue(
          params.headerDetailData.createdAt.replace("+07:00", ""),
          "DD MMMM YYYY, HH:mm"
        ) + " WIB",
      key: "createdAt",
      meta: {
        valueGap: 25,
        sectionGap: 30
      }
    }),
    new OptionsClass({
      name: t("dispatch.pdf.createdBy"),
      value: capitalize(params.headerDetailData.createdBy),
      key: "createdBy",
      meta: {
        valueGap: 25,
        sectionGap: 65
      }
    }),
    new OptionsClass({
      name: t("dispatch.pdf.totalSttKoli"),
      value: `${params.headerDetailData.totalStt}/${params.headerDetailData.totalPiece}`,
      key: "totalStt",
      meta: {
        valueGap: 20,
        sectionGap: 35
      }
    }),
    new OptionsClass({
      name: t("dispatch.courierOrShuttle"),
      value: mapCourierOrShuttleInfo(params.headerDetailData),
      key: "courierShuttle",
      meta: {
        valueGap: 25,
        sectionGap: 30
      }
    })
  ];
  // get & sort list header section data
  const sectionData = [];
  for (const section of params.headerSection) {
    for (const detail of headerSectionsData) {
      if (detail.key === section) {
        sectionData.push(detail);
      }
    }
  }
  // divide header section data to some row
  const totalColumnHeader = 4;
  const row =
    Math.ceil(params.headerSection.length / totalColumnHeader) +
    (params.headerSection.length <= totalColumnHeader ? 1 : 0);
  let i = 0;

  do {
    documentState.headerSection.push(sectionData.splice(0, row));
    i += 1;
  } while (i <= Math.floor(params.headerSection.length / row));

  documentState.startY += documentState.headerSection[0].length * 5;
  /* END HEADER SECTION DATA */
};


const tableDataProcess = (params: SectionManifestData) => {
  const i18n = createI18n({
    legacy: false,
    locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my },
  });
  const t = i18n.global.t;
  /* TABLE DATA */
  const tableColumnsData = [
    {
      header: t("dispatch.noStt"),
      dataKey: "sttNo"
    },
    {
      header: t("dispatch.noReferensi"),
      dataKey: "refNo"
    },
    {
      header: t("dispatch.jenisPengiriman"),
      dataKey: "productType"
    },
    {
      header: t("dispatch.deliveryType"),
      dataKey: "deliveryType"
    },
    {
      header: t("dispatch.piecesGrossWeight"),
      dataKey: "piecesGrossWeight"
    },
    {
      header: t("dispatch.noDem"),
      dataKey: "demNo"
    },
    {
      header: t("dispatch.ursaCode"),
      dataKey: "ursaCode"
    },
  ];

  // get & sort list column data
  for (const section of params.columns) {
    for (const detail of tableColumnsData) {
      if (detail.dataKey === section) {
        documentState.columnData.push(detail);
      }
    }
  }
  // add propery no each rows
  if (params.columns.length) {
    documentState.columnData.unshift({
      header: t("dispatch.no"),
      key: "no"
    });
  }
  //   add column No.
  params.data = params.data.map(
    (item: ColumnManifestData, index: number) =>
      new ColumnManifestData({
        ...item,
        no: index + 1
      })
  );
  /* END TABLE DATA */
};

// Add these interfaces near the top of the file
interface RenderPosition {
  x: number;
  y: number;
}

interface HeaderRenderConfig extends RenderPosition {
  xx: number;
}

interface ImageRenderConfig {
  totalMaxRight: number;
  y: number;
  spaceX?: number;
}

// Add this interface near other interfaces
interface ImageDimensions {
  width: number;
  height: number;
  yOffset?: number;
  identifier: string;
}

const renderImage = (
  config: ImageRenderConfig,
  dimensions: ImageDimensions,
  isBarcode = true
) => {
  const totalMaxRight =
    config.totalMaxRight - dimensions.width - (config.spaceX || 0);
  documentState.doc.addImage(
    isBarcode ? documentState.barcodeValue : documentState.qrcodeValue,
    "PNG",
    totalMaxRight,
    config.y - (dimensions.yOffset || 0),
    dimensions.width,
    dimensions.height,
    dimensions.identifier,
    "FAST",
    0
  );
  return totalMaxRight;
};

const renderQRCode = (config: ImageRenderConfig) => {
  const qrCodeSquare = 34;
  const dimensions: ImageDimensions = {
    width: qrCodeSquare,
    height: qrCodeSquare,
    yOffset: 2,
    identifier: "qrcode"
  };
  const totalMaxRight = renderImage(config, dimensions, false);
  return { totalMaxRight, qrCodeSquare };
};

const renderBarcode = (config: ImageRenderConfig) => {
  const dimensions: ImageDimensions = {
    width: 37,
    height: 13,
    yOffset: 0.8,
    identifier: "barcode"
  };
  return renderImage(config, dimensions);
};

const renderLogo = (config: ImageRenderConfig) => {
  const logoProperties = documentState.doc.getImageProperties(
    documentState.logo
  );
  const logoAspectRatio = logoProperties.height / logoProperties.width;
  const logoWidth = 40;
  documentState.doc.addImage(
    documentState.logo,
    "PNG",
    documentState.x,
    documentState.y,
    logoWidth,
    logoAspectRatio * logoWidth,
    `${documentState.roleType}-logo`,
    "FAST"
  );

  return documentState.y + 10;
};

const renderHeaderText = (position: RenderPosition) => {
  documentState.doc.setFont("Poppins", "bold");
  documentState.doc.setFontSize(14);
  documentState.doc.setTextColor("#4d4d4d");
  documentState.doc.text(documentState.title, position.x, position.y);
  return position.y + 4;
};

const renderHeaderDetails = (config: HeaderRenderConfig) => {
  const valueGap = 20;
  const sectionGap = 70;
  let { x, y, xx } = config;

  for (const header of documentState.headerSection) {
    documentState.doc.setFontSize(6);
    documentState.doc.setTextColor("#4d4d4d");
    documentState.doc.setFillColor("#ebecf0");

    for (const [idx, item] of header.entries()) {
      y += 5;
      documentState.doc.setFont("Poppins", "medium");
      documentState.doc.text(item.name, x, y);
      xx += item.meta?.valueGap || valueGap;
      documentState.doc.setFont("Poppins", "normal");
      documentState.doc.text(": " + item.value, xx, y);

      if (idx === header.length - 1) {
        x += item.meta?.sectionGap || sectionGap;
        xx = x;
        y -= header.length * 5;
      } else {
        xx -= item.meta?.valueGap || valueGap;
      }
    }
  }
};

// Update the onRenderHeaderdetail function to use the new interfaces
const onRenderHeaderdetail = () => {
  if (
    documentState.doc.internal.getNumberOfPages() !== 2 ||
    documentState.nextSection !== 1
  )
    return;

  let totalMaxRight = documentState.doc.internal.pageSize.width - margin.right;
  const spaceX = 0;

  // Render QR code and get updated positions
  const { totalMaxRight: qrRight, qrCodeSquare } = renderQRCode({
    totalMaxRight,
    y: documentState.y
  });
  totalMaxRight = qrRight;

  // First vertical line
  // totalMaxRight -= spaceX - 1;
  // documentState.doc.line(
  //   totalMaxRight,
  //   documentState.y,
  //   totalMaxRight,
  //   documentState.y + 20
  // );

  // Render barcode
  totalMaxRight = renderBarcode({
    totalMaxRight,
    y: documentState.y + 4,
    spaceX
  });

  // Second vertical line
  totalMaxRight -= spaceX;
  documentState.doc.line(
    totalMaxRight,
    documentState.y + 4,
    totalMaxRight,
    documentState.y + 15.5
  );

  // Render logo
  documentState.y = renderLogo({
    totalMaxRight,
    y: documentState.y,
    spaceX
  });

  // Render title and get updated y position
  documentState.y += 6;
  documentState.y = renderHeaderText({
    x: documentState.x,
    y: documentState.y
  });

  // Horizontal line
  documentState.doc.setLineWidth(0.2);
  documentState.doc.line(
    documentState.x,
    documentState.y,
    documentState.doc.internal.pageSize.width - margin.right - 32,
    documentState.y
  );

  // Render header details
  renderHeaderDetails({
    x: documentState.x,
    y: documentState.y,
    xx: documentState.xx
  });
};

const onWillDrawCell = (data: any) => {
  // add borders bottom each column
  documentState.doc.setDrawColor("#E0E0E0"); // set the border color
  documentState.doc.setLineWidth(0.2); // set the border with

  if (data.section === "head") {
    // draw top border each row
    documentState.doc.line(
      data.cell.x + data.cell.width,
      data.cell.y,
      data.cell.x,
      data.cell.y
    );
  }

  if (data.column.index === 0) {
    // draw left border
    documentState.doc.line(
      data.cell.x,
      data.cell.y + data.cell.height,
      data.cell.x,
      data.cell.y
    );
  } else if (data.column.index === documentState.columnData.length - 1) {
    // draw right border
    documentState.doc.line(
      data.cell.x + data.cell.width,
      data.cell.y,
      data.cell.x + data.cell.width,
      data.cell.y + data.cell.height
    );
  }

  if (data.section === "body" && data.column.dataKey === "sttNo") {
    const text = String(data.cell.raw ?? "");
    const baseX = data.cell.x + 1;
    const baseY = data.cell.y + data.cell.height - 2;

    // Bagi teks → 2 depan, tengah, 4 belakang
    const prefix = text.slice(0, 2);
    const middle = text.slice(2, -4);
    const suffix = text.slice(-4);

    let cursorX = baseX;

    // --- 2 huruf depan (BOLD) ---
    documentState.doc.setFont("Poppins", "bold");
    documentState.doc.setFontSize(8);
    documentState.doc.setTextColor(0, 0, 0);
    documentState.doc.text(prefix, cursorX, baseY);
    cursorX += documentState.doc.getTextWidth(prefix);

    // --- Tengah (NORMAL) ---
    documentState.doc.setFont("Poppins", "normal");
    documentState.doc.setFontSize(6);
    documentState.doc.setTextColor(80, 80, 80);
    documentState.doc.text(middle, cursorX, baseY);
    cursorX += documentState.doc.getTextWidth(middle);

    // --- 4 huruf belakang (BOLD) ---
    documentState.doc.setFont("Poppins", "bold");
    documentState.doc.setFontSize(8);
    documentState.doc.setTextColor(0, 0, 0);
    documentState.doc.text(suffix, cursorX, baseY);

    data.cell.text = [];
  }

  // draw bottom border each row
  documentState.doc.line(
    data.cell.x,
    data.cell.y + data.cell.height,
    data.cell.x + data.cell.width,
    data.cell.y + data.cell.height
  );
};

const onDidDrawPage = (data: any) => {
  onRenderHeaderdetail();
  documentState.currentSection = documentState.nextSection;
  documentState.nextSection = (documentState.nextSection % sections) + 1;

  // set left margin which will controll x position of next section
  // Calculate section width based on page width minus margins
  const pageWidth = documentState.doc.internal.pageSize.width;
const sectionWidth =
  (pageWidth - (margin.left + margin.right) - spacing) / sections;
const shift = (documentState.nextSection - 1) * (sectionWidth + spacing);
data.table.settings.margin.left = margin.left + shift;

  // if next section is not the fist, move to previous page so when
  // autoTable calls addPage() it will still be the same current page
  if (
    documentState.doc.internal.getNumberOfPages() === 2 &&
    documentState.nextSection === 1
  ) {
    data.table.settings.margin.top = margin.top;
  }

  if (documentState.nextSection > 1) {
    documentState.doc.setPage(
      documentState.doc.internal.getNumberOfPages() - 1
    );
  }

  /* END HEADER SECTION */
};

const onResetData = () => {
  documentState = {
    doc: documentState.doc,
    roleType: "",
    title: "",
    headerSection: [],
    columnData: [],
    x: margin.left,
    y: margin.top,
    xx: margin.left,
    currentSection: 1,
    nextSection: 1,
    startY: margin.top + 23,
    logo: "",
    qrcodeValue: "",
    barcodeValue: ""
  };
};

const initializeDocument = (): void => {
  documentState.doc.addPage(); // Initial empty page needed for setPage
};

const generateBarcodeImage = (keyValue: string): string => {
  const barcodeId = "barcode";
  document.getElementById(barcodeId)?.remove();

  if (!keyValue) return "";

  const barcodeImg = document.createElement("img");
  barcodeImg.setAttribute("id", barcodeId);
  barcodeImg.setAttribute("style", "display: none;");
  barcodeImg.setAttribute("alt", "barcode");
  JsBarcode(barcodeImg, keyValue, { displayValue: false });
  document.body.appendChild(barcodeImg);

  return document.getElementById("barcode")?.getAttribute("src") || "";
};

const getAutoTableConfig = (params: SectionManifestData): any => ({
  theme: "plain" as const,
  body: params.data,
  columns: documentState.columnData,
  styles: {
    font: "Poppins",
    halign: "left",
    fontSize: 6,
    textColor: "#4D4D4D",
  },
  headStyles: {
    fontStyle: "semibold",
    fillColor: "#F5F6F7",
    cellPadding: { top: 2, right: 1, bottom: 2, left: 1 }
  },
  columnStyles: {
    no: { cellWidth: 10 },
    sttNo: { cellWidth: 32, minCellHeight: 6 },
    refNo: { minCellWidth: 32 },
    productType: { minCellWidth: 30 },
    deliveryType: { minCellWidth: 20 },
    piecesGrossWeight: { minCellWidth: 20 },
    demNo: { minCellWidth: 32 },
    ursaCode: { minCellWidth: 18 }
  },
  bodyStyles: {
    fontStyle: "normal",
    valign: "middle",
    cellPadding: { top: 1.2, right: 1.3, bottom: 1.2, left: 1.3 },
    cellWidth: "auto",
    lineColor: "#4D4D4D"
  },
  // tableWidth:
  //   (documentState.doc.internal.pageSize.width -
  //     (margin.left + margin.right) -
  //     spacing) /
  //   sections,
  margin: {
    left:
      margin.left +
      (documentState.nextSection - 1) *
        ((documentState.doc.internal.pageSize.width -
          (margin.left + margin.right) -
          spacing) /
          sections +
          spacing),
    top: documentState.startY,
    bottom: margin.bottom
  },
  startY: documentState.startY,
  rowPageBreak: "avoid" as const, // Fix: Add type assertion
  didDrawPage: onDidDrawPage,
  willDrawCell: onWillDrawCell
});

const finalizeDocument = (): void => {
  documentState.doc.setPage(documentState.doc.internal.getNumberOfPages());
  documentState.doc.deletePage(1);
  documentState.doc.setProperties({ title: documentState.title });
};

const mapDeliveryType = (data: DispatchStt) => {
  if (data.ddIsCod && !data.ddIsDfod) return "COD";
  if (data.ddIsDfod) return "DFOD";
  return "-";
};

export const generateManifest2ColumnPotrait = async (
  doc: PDFDocument | any,
  detailData: Dispatch
): Promise<void> => {
  documentState.doc = doc;
  initializeDocument();

  const i18n = createI18n({
    legacy: false,
    locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my },
  });
  const t = i18n.global.t;

  const accountData = AccountController.accountData;

  const params = new SectionManifestData({
    featureName: "Dispatch",
    headerSection: [
      "destinationCity",
      "createdBy",
      "totalPiece",
      "totalStt",
      "createdAt",
      "courierShuttle"
    ],
    headerDetailData: new HeaderManifestData({
      destinationCity: `${accountData.account_location.city_code} - ${accountData.account_location.city_name}`,
      createdBy: `${ellipsisString(detailData.dispatchCreatedByName, 20)} - ${
        detailData.dispatchCreatedByRole
      }`,
      createdAt: detailData.dispatchCreatedAt.replace("+07:00", ""),
      totalStt: detailData.dispatchStts,
      totalPiece: detailData.dispatchSttPieces,
      key: new OptionsClass({
        name: t("dispatch.noDispatch"),
        value: detailData.dispatchNo
      }),
      totalGrossWeight: detailData.dispatchSttGrossWeights,
      qrCodeValue: detailData.dispatchNo,
      isFormatDateUsingTimestamp: true,
      dispatchToCityCode: detailData.dispatchToCityCode,
      dispatchToDriverId: detailData.dispatchToDriverId,
      dispatchToDriverName: detailData.dispatchToDriverName,
      dispatchToDriverPhone: detailData.dispatchToDriverPhone,
      dispatchToHubId: detailData.dispatchToHubId,
      dispatchToHubName: detailData.dispatchToHubName,
      dispatchToShuttleId: detailData.dispatchToShuttleId,
      dispatchToShuttleName: detailData.dispatchToShuttleName 
    }),
    columns: [
      "sttNo",
      "refNo",
      "productType",
      "deliveryType",
      "piecesGrossWeight",
      "demNo",
      "ursaCode"
    ],

    data: detailData.stts.map(
      (el: DispatchStt) =>
        new ColumnManifestData({
          sttNo: el.ddSttNo,
          refNo: el.ddRefNo || "-",
          productType: el.ddProductType,
          deliveryType: mapDeliveryType(el),
          piecesGrossWeight: `${el.ddPieces}/${el.ddGrossWeight}kg`,
          demNo: el.ddDemNo || "-",
          ursaCode: el.ddDestinationUrsaCode,
        })
    ),
    signature: false
  });

  // Initialize content
  documentState.qrcodeValue = await convertQRCode(
    params.headerDetailData.qrCodeValue
  );
  documentState.barcodeValue = generateBarcodeImage(
    params.headerDetailData.key.value
  );

  await headerSectionProcess(params);
  tableDataProcess(params);

  // Load logo
  const importLogo = await import(
    `@/app/ui/assets/images/logo-${documentState.roleType}.png`
  );
  documentState.logo = importLogo.default;

  // Generate table
  autoTable(documentState.doc, getAutoTableConfig(params));

  finalizeDocument();
  onResetData();
};
